import { Box, Divider, Slider, Stack, Switch, Typography } from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import sidebarBg from "../../../../../../assets/sidebar-bg.jpg";
import ColorPicker from "../../../../../../components/ColorPicker/ColorPicker";
import { ImageUploader } from "../../../../../../components/ImageUploader/ImageUploader";
import {
  AttributeKey,
  PageUid,
  useCustomisation,
} from "../../../../../../context/CustomisationContext";
import { usePortal } from "../../../../../../context/PortalsContext";
import { useToast } from "../../../../../../context/ToastContext";
import { useTranslation } from "../../../../../../context/TranslationContext";
import {
  getS3PresignedUrl,
  updateSectionDesign,
} from "../../../../../../services/api/apiClient";
import {
  GetS3PresignedUrlResult,
  PresignFileType,
} from "../../../../../../services/api/types/portalConfiguration/uploadPhoto";
import { brand, gray } from "../../../../../../theme/themePrimitives";

interface BackgroundCustomisationProps {
  pageId?: PageUid;
}

export const BackgroundCustomisation: React.FC<
  BackgroundCustomisationProps
> = ({ pageId = "GPC" }) => {
  const { updateCustomisationSection, getCustomisationValue } =
    useCustomisation();
  const { portals } = usePortal();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const { translations } = useTranslation();
  const backgroundTranslations = translations.translations["portal-design"].gpc;

  const bgImageToggleKey =
    `${pageId.toLowerCase()}BackgroundImageToggle` as AttributeKey;

  const isLoginPage = pageId === "LOGIN";

  const showUploadImage =
    !isLoginPage &&
    getCustomisationValue(pageId, "backgroundSettings", bgImageToggleKey) ===
      "true";
  const backgroundImageTransparencyKey = `${pageId.toLowerCase()}BackgroundImageTransparency`;
  const primaryBgColorKey = `${pageId.toLowerCase()}PrimaryBgColor`;
  const secondaryBgColorKey = `${pageId.toLowerCase()}SecondaryBgColor`;
  const sidebarBgColorKey = `${pageId.toLowerCase()}SidebarBgColor`;

  const handleAttributeChange = (
    attributeKey: string,
    newAttributeValue?: string,
  ) => {
    if (!newAttributeValue) return;

    updateCustomisationSection(pageId, "backgroundSettings", {
      [attributeKey]: newAttributeValue,
    });

    updateSectionDesign(
      portals.selectedPortal?.portalUid!,
      pageId,
      "backgroundSettings",
      {
        attributeKey,
        attributeValue: newAttributeValue,
      },
    );
  };

  const handleImageUpload = async (resizedFile: File) => {
    if (/[() %+#$&@[\]{},]/.test(resizedFile.name)) {
      showToast(
        "Upload failed. The filename contains unsupported characters. Please rename the file using accepted characters and try again.",
        "error",
      );
      return;
    }

    try {
      setLoading(true);
      const response = (await getS3PresignedUrl(
        portals.selectedPortal?.portalUid!,
        {
          fileName: resizedFile.name,
          contentType: resizedFile.type,
          fileType: PresignFileType.ASSET,
        },
      )) as GetS3PresignedUrlResult;
      const { uploadUrl, path } = response;
      await axios.put(uploadUrl, resizedFile, {
        headers: {
          "Content-Type": resizedFile.type,
        },
      });

      handleAttributeChange(
        `${pageId.toLowerCase()}BackgroundImage` as AttributeKey,
        path,
      );
    } catch (error: any) {
      console.log("Error:", error);
      showToast(error.response.data, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          {backgroundTranslations?.backgroundTitle}
        </Typography>
      </Box>
      <Divider />
      <Stack spacing={1}>
        {!isLoginPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography fontSize={"16px"} fontWeight="bold">
                {backgroundTranslations?.pageBackgroundImageTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {backgroundTranslations?.pageBackgroundImageSubtitle}
              </Typography>
            </Box>
            <Switch
              checked={showUploadImage}
              onChange={(e) =>
                handleAttributeChange(
                  bgImageToggleKey,
                  e.target.checked ? "true" : "false",
                )
              }
            />
          </Box>
        )}
        {showUploadImage && !isLoginPage && (
          <Box
            sx={{
              border: `1px solid ${gray[300]}`,
              borderRadius: "8px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              p: 2,
              gap: 2,
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <ImageUploader
                  type="backgroundImage"
                  pageId={pageId}
                  label="Upload image"
                  onImageResized={(file) => {
                    handleImageUpload(file);
                  }}
                  loading={loading}
                />
              </Box>
              <Box>
                <Typography mt={3}>
                  {backgroundTranslations?.backgroundTransparencyText}
                </Typography>
                <Slider
                  value={
                    (getCustomisationValue(
                      pageId,
                      "backgroundSettings",
                      backgroundImageTransparencyKey as AttributeKey,
                    ) ?? "0") as unknown as number
                  }
                  onChangeCommitted={(event, newValue) =>
                    handleAttributeChange(
                      backgroundImageTransparencyKey,
                      newValue.toString(),
                    )
                  }
                  step={1}
                  min={0}
                  max={100}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  sx={{
                    color: brand[600],
                    "& .MuiSlider-thumb": {
                      backgroundColor: "white",
                      border: "2px solid",
                      borderColor: brand[600],
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "0px 0px 0px 6px rgba(0, 0, 0, 0.1)",
                      },
                      "&.Mui-active": {
                        boxShadow: "0px 0px 0px 10px rgba(0, 0, 0, 0.15)",
                      },
                    },
                    "& .MuiSlider-track": {
                      backgroundColor: brand[600],
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#e0e0e0",
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                backgroundColor: gray[200],
                borderRadius: "8px",
                width: "100%",
                height: 250,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography>{backgroundTranslations?.primaryText} BG</Typography>
            </Box>
          </Box>
        )}
      </Stack>
      <Stack spacing={1}>
        <Typography fontSize={"16px"} fontWeight="bold">
          Page background Color
        </Typography>
        {!showUploadImage && (
          <Box
            sx={{
              border: `1px solid ${gray[300]}`,
              borderRadius: "8px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              p: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>{backgroundTranslations?.primaryText} BG</Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "backgroundSettings",
                  primaryBgColorKey as AttributeKey,
                )}
                onChange={(color) =>
                  handleAttributeChange(primaryBgColorKey, color)
                }
              />
            </Box>
            <Box
              sx={{
                backgroundColor: gray[200],
                borderRadius: "8px",
                height: 200,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography>{backgroundTranslations?.primaryText} BG</Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            border: `1px solid ${gray[300]}`,
            borderRadius: "8px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <Typography>{backgroundTranslations?.secondaryText} BG</Typography>
            <ColorPicker
              value={getCustomisationValue(
                pageId,
                "backgroundSettings",
                secondaryBgColorKey as AttributeKey,
              )}
              onChange={(color) =>
                handleAttributeChange(secondaryBgColorKey, color)
              }
            />
          </Box>
          <Box
            sx={{
              backgroundColor: gray[200],
              borderRadius: "8px",
              height: 200,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ padding: "3rem", backgroundColor: "white" }}>
              {backgroundTranslations?.secondaryText} BG
            </Typography>
          </Box>
        </Box>
      </Stack>
      {pageId === "GPC" && (
        <Stack spacing={1}>
          <Typography fontSize={"16px"} fontWeight="bold">
            {backgroundTranslations?.sidebarMenuTitle}
          </Typography>
          <Box
            sx={{
              border: `1px solid ${gray[300]}`,
              borderRadius: "8px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              p: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography>Sidebar BG</Typography>
              <ColorPicker
                value={getCustomisationValue(
                  pageId,
                  "backgroundSettings",
                  sidebarBgColorKey as AttributeKey,
                )}
                onChange={(color) =>
                  handleAttributeChange(sidebarBgColorKey, color)
                }
              />
            </Box>
            <Box
              sx={{ width: "100%", height: "200px" }}
              component={"img"}
              src={sidebarBg}
              alt="sidebar-bg"
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
